// import jalaali from 'jalaali-js';
import dayjs from "dayjs";
import jalaliday from "jalaliday";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(jalaliday);
dayjs.extend(utc);
dayjs.extend(timezone);

interface IFormatSecondsConfig {
  days?: boolean;
  hours?: boolean;
  minutes?: boolean;
  seconds?: boolean;
}

/**
 * Converts seconds into days, hours, minutes, and seconds.
 *
 * @returns Returns the formatted time as a string.
 */
export function formatSeconds(
  seconds?: number,
  short?: boolean,
  config?: IFormatSecondsConfig
) {
  if (seconds === 0) return `0${short ? "s" : "seconds"}`;
  if (seconds === undefined) return "?";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor(((seconds % 86400) % 3600) / 60);
  const s = Math.floor(((seconds % 86400) % 3600) % 60);

  const daysDisplay = d > 0 ? d + (short ? "d" : "days") : "";
  const hoursDisplay = h > 0 ? h + (short ? "h" : "hours") : "";
  const minutesDisplay = m > 0 ? m + (short ? "m" : "minutes") : "";
  const secondsDisplay = s > 0 ? s + (short ? "s" : "seconds") : "";

  const formatted: string[] = [];
  if (config?.days) formatted.push(daysDisplay);
  if (config?.hours) formatted.push(hoursDisplay);
  if (config?.minutes) formatted.push(minutesDisplay);
  if (config?.seconds) formatted.push(secondsDisplay);
  return formatted.join(" ");
}

/**
 * Formats a Date object into "m/d/y, h:m:s AM/PM"
 *
 * @returns Returns the formatted date as a string.
 */
export function formatDate(dateInput?: number, dateString?: string) {
  let dateInstance;

  if (dateString) {
    // If dateString is provided, trim it and append 'Z' to ensure UTC parsing
    const trimmedDateString = `${dateString.slice(0, 23)}Z`;
    dateInstance = new Date(trimmedDateString);
  } else if (dateInput) {
    // If a timestamp is provided, use it to create the date instance
    dateInstance = new Date(dateInput);
  } else {
    // If neither is provided, use the current date and time
    dateInstance = new Date();
  }

  // Get the user's local timezone from the Intl API
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Format the date according to the user's locale and timezone
  return Intl.DateTimeFormat("default", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    timeZone: userTimezone, // Ensure the date is formatted in the user's timezone
  }).format(dateInstance);
}

export function formatDateString(
  dateString?: string,
  detail: string = "daily",
  direction: Direction = "rtl"
): string {
  if (!dateString) {
    return "";
  }

  // Use Tehran timezone for RTL/Jalali calendar, user's local timezone for Gregorian
  const userTimezone = direction === "rtl" ? "Asia/Tehran" : Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  if (direction === "rtl") {
    try {
      // Parse UTC date and convert to Tehran timezone
      const tehranDate = dayjs.utc(dateString).tz("Asia/Tehran");
      
      // Get individual date components from Tehran time
      const y = tehranDate.year();
      const m = tehranDate.month() + 1; // Convert to 1-indexed for string format
      const d = tehranDate.date();
      const h = tehranDate.hour();
      const min = tehranDate.minute();
      
      // Parse the date in UTC mode to avoid browser timezone offset issues
      const dateOnlyStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      
      // Use the Tehran date directly for Jalali conversion
      const gregorianDate = dayjs.utc(`${dateOnlyStr}T${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}:00Z`);
      
      // Convert to Jalali calendar
      const jalaliDate = gregorianDate.calendar("jalali");

      if (detail === "daily") {
        return jalaliDate.format("MM/DD");
      } else if (detail === "hourly") {
        return jalaliDate.format("HH:mm");
      }
      // Full detail
      return jalaliDate.format("YYYY/MM/DD HH:mm");
    } catch (error) {
      console.error("Error converting to Jalali:", error);
      return ""; // Return empty string on conversion error
    }
  }

  // For non-RTL (Gregorian), parse the date safely
  let parsedDate: Date;
  try {
    // Try parsing as ISO string
    const trimmedDateString = dateString.endsWith("Z")
      ? dateString
      : `${dateString.slice(0, 23)}Z`;
    parsedDate = new Date(trimmedDateString);

    // Check if the date is valid
    if (Number.isNaN(parsedDate.getTime())) {
      throw new TypeError("Invalid date");
    }
  } catch (error) {
    // If parsing fails, try using dayjs
    const dayjsDate = dayjs(dateString);
    if (!dayjsDate.isValid()) {
      return ""; // Return empty string for invalid dates
    }
    parsedDate = dayjsDate.toDate();
  }

  // Use the parsed date for Gregorian formatting
  const userDate = parsedDate;

  // Gregorian calendar formatting
  try {
    switch (detail) {
      case "full":
        return Intl.DateTimeFormat(undefined, {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          timeZone: userTimezone,
        }).format(userDate);

      case "daily":
        return Intl.DateTimeFormat(undefined, {
          month: "2-digit",
          day: "2-digit",
          timeZone: userTimezone,
        }).format(userDate);

      case "hourly":
        return Intl.DateTimeFormat(undefined, {
          hour: "2-digit",
          minute: "2-digit",
          timeZone: userTimezone,
        }).format(userDate);

      default:
        return Intl.DateTimeFormat(undefined, {
          timeZone: userTimezone,
        }).format(userDate);
    }
  } catch (error) {
    console.error("Error formatting date:", error);
    return ""; // Return empty string on formatting error
  }
}
// utils/formatNumber.ts
export function formatNumberWithCommas(
  num: number,
  digits: number = 0
): string | any {
  // Check if the input is a valid number
  if (!num) return num;

  if (Number.isNaN(num)) {
    return "";
  }

  // Round the number to the specified number of decimal places
  const roundedNum = Number(num.toFixed(digits));

  // Convert the number to a string and use regex to add commas
  return roundedNum.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
export const formatFilenameDateString = (
  date: string,
  direction: Direction = "rtl"
): string => {
  const isJalali = direction === "rtl"; // Or however you determine Jalali preference

  const d = dayjs(date);

  if (isJalali) {
    // Convert to Jalali and format
    return d
      .calendar("jalali")
      .format("YYYY-MM-DD-HHmm")
      .replace(/[۰-۹]/g, (da: string) =>
        String(da.charCodeAt(0) - "۰".charCodeAt(0))
      );
  }

  // Regular Gregorian format
  return d.format("YYYY-MM-DD-HHmm");
};

export const formatMonthName = (datetime: string): string => {
  const date = new Date(datetime);
  return date.toLocaleString("default", { month: "long" });
};

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
}
